import type { Dispatch } from '@reduxjs/toolkit';
import { refreshSessionToken } from './refreshSessionToken';

type FetchHeaders = RequestInit['headers'];

function headersToRecord(headers: FetchHeaders): Record<string, string> {
  if (headers === undefined) {
    return {};
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  if (typeof (headers as Headers).forEach === 'function') {
    const record: Record<string, string> = {};
    (headers as Headers).forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  return { ...(headers as Record<string, string>) };
}

function bearerTokenOf(headers: Record<string, string>): string | null {
  const name = Object.keys(headers).find((key) => key.toLowerCase() === 'authorization');
  const value = name === undefined ? undefined : headers[name];
  if (value === undefined || !value.startsWith('Bearer ')) {
    return null;
  }
  return value.slice('Bearer '.length);
}

/**
 * A `fetch` for the music24-api tRPC client. On a 401 it refreshes the session once and sends the
 * request again with the new token, as `customBaseQuery` does for REST. A lost membership answers
 * 401; the refresh then gives a token for the home workspace (`WorkspaceChangeReset` resets the
 * caches). The refresh is shared with every other caller (`refreshSessionToken`). A 401 of an admin
 * session ends that session: its token cannot be refreshed.
 */
export function createRefreshingFetch(
  dispatch: Dispatch
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return async (input, init) => {
    const response = await window.fetch(input, init);
    if (response.status !== 401) {
      return response;
    }
    const headers = headersToRecord(init?.headers);
    const token = bearerTokenOf(headers);
    if (token === null) {
      return response;
    }
    const outcome = await refreshSessionToken(token, dispatch, { unauthorized: true });
    if (outcome.kind !== 'refreshed') {
      return response;
    }
    const retryHeaders = Object.fromEntries(
      Object.entries(headers).filter(([key]) => key.toLowerCase() !== 'authorization')
    );
    return window.fetch(input, { ...init, headers: { ...retryHeaders, Authorization: `Bearer ${outcome.token}` } });
  };
}
